import type {
  BuilderField,
  BuilderFieldType,
  BuilderSchema,
  BuilderSection,
} from "@/api/form-builder-templates/form-builder-templates.types";
import { PublishTemplateModal } from "@/components/features/FormBuilderTemplates/PublishTemplateModal";
import { PreviewSections } from "@/components/features/FormBuilderTemplates/TemplatePreviewModal";
import {
  FIELD_TYPE_OPTIONS,
  getFormTypeBadgeProps,
  getFormTypeLabel,
  getVersionStatusBadgeProps,
  getVersionStatusLabel,
} from "@/components/features/FormBuilderTemplates/templateConfig";
import { Button } from "@/components/ui/Button/Button";
import Loader from "@/components/ui/Loader/Loader";
import { Modal } from "@/components/ui/Modal/Modal";
import {
  DISPATCH_BINDING_OPTIONS,
  getDispatchBindingLabel,
} from "@/constants/dispatchScriptBindings";
import {
  usePublishTemplateVersion,
  useSaveTemplateVersion,
} from "@/hooks/form-builder-templates/useFormTemplateMutations";
import { useFormTemplateVersion } from "@/hooks/form-builder-templates/useFormTemplateQueries";
import { Badge } from "@onengine/component-library";
import { Checkbox, Group, Select, Stack, Text, TextInput, Title, Tooltip } from "@mantine/core";
import { IconChevronLeft, IconEye, IconPlus, IconTrash } from "@tabler/icons-react";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import styles from "./edit.module.scss";

const editSearchSchema = z.object({
  versionId: z.string().optional(),
});

export const Route = createFileRoute("/form-builder/_formBuilderLayout/templates/$id/edit")({
  component: TemplateEditorPage,
  validateSearch: editSearchSchema.parse,
  beforeLoad: ({ search }) => {
    // The editor always needs a concrete version — entries from the list/drawer
    // provide one; bare URLs get sent back to the templates list.
    if (!search.versionId) {
      throw redirect({ to: "/form-builder/templates" });
    }
  },
});

const OPTION_FIELD_TYPES: BuilderFieldType[] = ["select", "radio"];

const BINDING_SELECT_DATA = DISPATCH_BINDING_OPTIONS.reduce<
  { group: string; items: { value: string; label: string }[] }[]
>((groups, option) => {
  const group = groups.find((candidate) => candidate.group === option.group);
  const item = { value: option.value, label: option.label };
  if (group) {
    group.items.push(item);
  } else {
    groups.push({ group: option.group, items: [item] });
  }
  return groups;
}, []);

const newFieldKey = () => `field_${crypto.randomUUID().slice(0, 8)}`;
const newSectionKey = () => `section_${crypto.randomUUID().slice(0, 8)}`;

const makeField = (): BuilderField => ({
  key: newFieldKey(),
  label: "",
  formControlType: "input",
  required: false,
});

const makeSection = (index: number): BuilderSection => ({
  key: newSectionKey(),
  title: `Section ${index + 1}`,
  fields: [makeField()],
});

const normalizeSchema = (schema: unknown): BuilderSection[] => {
  const sections = (schema as { sections?: unknown } | null | undefined)?.sections;
  if (!Array.isArray(sections)) return [];
  return sections.map((section, sectionIndex) => {
    const raw = (section ?? {}) as Partial<BuilderSection>;
    const fields = Array.isArray(raw.fields) ? raw.fields : [];
    return {
      key: typeof raw.key === "string" && raw.key ? raw.key : `section_${sectionIndex}`,
      title: typeof raw.title === "string" ? raw.title : `Section ${sectionIndex + 1}`,
      fields: fields.map((field, fieldIndex) => {
        const rawField = (field ?? {}) as Partial<BuilderField> & { type?: string };
        const type = (rawField.formControlType ?? rawField.type ?? "input") as BuilderFieldType;
        return {
          key:
            typeof rawField.key === "string" && rawField.key
              ? rawField.key
              : `field_${sectionIndex}_${fieldIndex}`,
          label: typeof rawField.label === "string" ? rawField.label : "",
          formControlType: FIELD_TYPE_OPTIONS.some((option) => option.value === type)
            ? type
            : "input",
          required: Boolean(rawField.required),
          ...(typeof rawField.placeholder === "string"
            ? { placeholder: rawField.placeholder }
            : {}),
          ...(Array.isArray(rawField.options)
            ? { options: rawField.options.map((option) => String(option)) }
            : {}),
          ...(typeof rawField.binding === "string" && rawField.binding
            ? { binding: rawField.binding }
            : {}),
        };
      }),
    };
  });
};

function TemplateEditorPage() {
  const navigate = useNavigate();
  const { id } = Route.useParams();
  const { versionId } = Route.useSearch();

  const versionQuery = useFormTemplateVersion(id, versionId);
  const saveMutation = useSaveTemplateVersion();
  const publishMutation = usePublishTemplateVersion();

  const [sections, setSections] = useState<BuilderSection[]>([]);
  const [dirty, setDirty] = useState(false);
  const [publishOpened, setPublishOpened] = useState(false);
  const [previewOpened, setPreviewOpened] = useState(false);
  const [loadedVersionId, setLoadedVersionId] = useState<string | null>(null);

  const version = versionQuery.data;

  useEffect(() => {
    if (version && version.id !== loadedVersionId) {
      setSections(normalizeSchema(version.schema));
      setDirty(false);
      setLoadedVersionId(version.id);
    }
  }, [version, loadedVersionId]);

  const fieldCount = useMemo(
    () => sections.reduce((sum, section) => sum + section.fields.length, 0),
    [sections],
  );

  const hasUnboundDataField = useMemo(
    () =>
      sections.some((section) =>
        section.fields.some((field) => field.formControlType === "data_field" && !field.binding),
      ),
    [sections],
  );

  const updateSections = (updater: (previous: BuilderSection[]) => BuilderSection[]) => {
    setSections(updater);
    setDirty(true);
  };

  const updateField = (sectionKey: string, fieldKey: string, patch: Partial<BuilderField>) => {
    updateSections((previous) =>
      previous.map((section) =>
        section.key === sectionKey
          ? {
              ...section,
              fields: section.fields.map((field) =>
                field.key === fieldKey ? { ...field, ...patch } : field,
              ),
            }
          : section,
      ),
    );
  };

  const buildSchema = (): BuilderSchema => ({
    sections: sections.map((section) => ({
      ...section,
      fields: section.fields.map((field) => {
        if (field.formControlType === "data_field") {
          const cleaned: BuilderField = {
            key: field.key,
            label: field.label,
            formControlType: field.formControlType,
            required: false,
          };
          if (field.binding) cleaned.binding = field.binding;
          return cleaned;
        }
        const cleaned: BuilderField = {
          key: field.key,
          label: field.label,
          formControlType: field.formControlType,
          required: field.required,
        };
        if (field.placeholder) cleaned.placeholder = field.placeholder;
        if (OPTION_FIELD_TYPES.includes(field.formControlType) && field.options?.length) {
          cleaned.options = field.options;
        }
        return cleaned;
      }),
    })),
  });

  const handleSave = (onSaved?: (savedVersionId: string) => void) => {
    if (!versionId) return;
    saveMutation.mutate(
      { templateId: id, versionId, schema: buildSchema() },
      {
        onSuccess: (saved) => {
          setDirty(false);
          // Editing a published version creates a new draft (copy-on-write):
          // move the editor onto the returned draft version.
          if (saved.id !== versionId) {
            setLoadedVersionId(saved.id);
            setSections(normalizeSchema(saved.schema));
            navigate({
              to: "/form-builder/templates/$id/edit",
              params: { id },
              search: { versionId: saved.id },
              replace: true,
            });
          }
          onSaved?.(saved.id);
        },
      },
    );
  };

  const handlePublish = () => {
    if (!versionId) return;
    const publish = (targetVersionId: string) => {
      publishMutation.mutate(
        { templateId: id, versionId: targetVersionId },
        {
          onSuccess: () => {
            setPublishOpened(false);
            navigate({ to: "/form-builder/templates" });
          },
        },
      );
    };
    if (dirty) {
      handleSave((savedVersionId) => publish(savedVersionId));
    } else {
      publish(versionId);
    }
  };

  if (versionQuery.isLoading) {
    return <Loader />;
  }

  if (versionQuery.isError || !version) {
    return (
      <Stack align="center" py="xl" gap="sm">
        <Text c="dimmed">Unable to load this form version.</Text>
        <Button variant="secondary" onClick={() => navigate({ to: "/form-builder/templates" })}>
          Back to Form Templates
        </Button>
      </Stack>
    );
  }

  const isPublished = version.status === "published";

  return (
    <>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button
            type="button"
            className={styles.backButton}
            onClick={() => navigate({ to: "/form-builder/templates" })}
            aria-label="Back to Form Templates"
          >
            <IconChevronLeft size={18} />
          </button>
          <Title
            order={3}
            style={{
              fontWeight: "var(--font-weight-bold)",
              fontSize: "var(--heading-font-size-h3)",
            }}
          >
            {version.templateName}
          </Title>
          <Badge {...getFormTypeBadgeProps(version.formType)}>
            {getFormTypeLabel(version.formType)}
          </Badge>
          <Badge {...getVersionStatusBadgeProps(version.status)}>
            {`V${version.version} • ${getVersionStatusLabel(version.status)}`}
          </Badge>
        </div>
        <Group gap="sm">
          <Button
            variant="tertiary"
            iconLeft={<IconEye size={16} />}
            onClick={() => setPreviewOpened(true)}
          >
            Preview
          </Button>
          <Button
            variant="secondary"
            onClick={() => handleSave()}
            disabled={saveMutation.isPending || !dirty}
          >
            {isPublished ? "Save as New Draft" : "Save Draft"}
          </Button>
          {!isPublished || dirty ? (
            <Tooltip
              label="Every Data Field needs a bound value before publishing"
              disabled={!hasUnboundDataField}
            >
              <span>
                <Button
                  variant="primary"
                  onClick={() => setPublishOpened(true)}
                  disabled={publishMutation.isPending || hasUnboundDataField}
                >
                  Publish
                </Button>
              </span>
            </Tooltip>
          ) : null}
        </Group>
      </div>

      {isPublished ? (
        <Text size="sm" c="dimmed" mb="sm">
          This version is published. Saving your changes will create a new draft version.
        </Text>
      ) : null}

      {sections.length === 0 ? (
        <div className={styles.emptyState}>
          <Text mb="sm">No sections yet. Add a section to start building this form.</Text>
        </div>
      ) : (
        sections.map((section) => (
          <div key={section.key} className={styles.sectionCard}>
            <div className={styles.sectionHeader}>
              <TextInput
                value={section.title}
                onChange={(event) => {
                  const title = event.currentTarget.value;
                  updateSections((previous) =>
                    previous.map((item) => (item.key === section.key ? { ...item, title } : item)),
                  );
                }}
                placeholder="Section title"
                size="sm"
                style={{ flex: 1, maxWidth: 420 }}
              />
              <Tooltip label="Remove section">
                <button
                  type="button"
                  className={styles.removeButton}
                  onClick={() =>
                    updateSections((previous) =>
                      previous.filter((item) => item.key !== section.key),
                    )
                  }
                  aria-label="Remove section"
                >
                  <IconTrash size={16} />
                </button>
              </Tooltip>
            </div>

            {section.fields.map((field) => (
              <div key={field.key} className={styles.fieldRow}>
                <TextInput
                  label="Field Label"
                  value={field.label}
                  onChange={(event) =>
                    updateField(section.key, field.key, { label: event.currentTarget.value })
                  }
                  placeholder="Enter label"
                  size="sm"
                />
                <Select
                  label="Field Type"
                  value={field.formControlType}
                  onChange={(value) => {
                    const nextType = (value ?? "input") as BuilderFieldType;
                    updateField(
                      section.key,
                      field.key,
                      nextType === "data_field"
                        ? {
                            formControlType: nextType,
                            required: false,
                            placeholder: undefined,
                            options: undefined,
                          }
                        : { formControlType: nextType, binding: undefined },
                    );
                  }}
                  data={
                    version.formType === "dispatch_call"
                      ? FIELD_TYPE_OPTIONS
                      : FIELD_TYPE_OPTIONS.filter((option) => option.value !== "data_field")
                  }
                  allowDeselect={false}
                  size="sm"
                />
                {field.formControlType === "data_field" ? (
                  <Select
                    label="Bound value"
                    value={field.binding ?? null}
                    onChange={(value) =>
                      updateField(section.key, field.key, { binding: value ?? undefined })
                    }
                    data={BINDING_SELECT_DATA}
                    placeholder="Select.."
                    error={field.binding ? undefined : "Select a bound value before publishing"}
                    size="sm"
                  />
                ) : OPTION_FIELD_TYPES.includes(field.formControlType) ? (
                  <TextInput
                    label="Options (comma separated)"
                    value={(field.options ?? []).join(", ")}
                    onChange={(event) =>
                      updateField(section.key, field.key, {
                        options: event.currentTarget.value
                          .split(",")
                          .map((option) => option.trim())
                          .filter(Boolean),
                      })
                    }
                    placeholder="Option 1, Option 2"
                    size="sm"
                  />
                ) : (
                  <TextInput
                    label="Placeholder"
                    value={field.placeholder ?? ""}
                    onChange={(event) =>
                      updateField(section.key, field.key, {
                        placeholder: event.currentTarget.value,
                      })
                    }
                    placeholder="Optional"
                    size="sm"
                  />
                )}
                {field.formControlType === "data_field" ? (
                  <Text size="sm" c="dimmed" className={styles.fieldRowRequired}>
                    {getDispatchBindingLabel(field.binding)}
                  </Text>
                ) : (
                  <Checkbox
                    className={styles.fieldRowRequired}
                    label="Required"
                    checked={Boolean(field.required)}
                    onChange={(event) =>
                      updateField(section.key, field.key, {
                        required: event.currentTarget.checked,
                      })
                    }
                  />
                )}
                <Tooltip label="Remove field">
                  <button
                    type="button"
                    className={styles.removeButton}
                    onClick={() =>
                      updateSections((previous) =>
                        previous.map((item) =>
                          item.key === section.key
                            ? {
                                ...item,
                                fields: item.fields.filter(
                                  (candidate) => candidate.key !== field.key,
                                ),
                              }
                            : item,
                        ),
                      )
                    }
                    aria-label="Remove field"
                  >
                    <IconTrash size={16} />
                  </button>
                </Tooltip>
              </div>
            ))}

            <Group mt="sm">
              <Button
                variant="tertiary"
                iconLeft={<IconPlus size={16} />}
                onClick={() =>
                  updateSections((previous) =>
                    previous.map((item) =>
                      item.key === section.key
                        ? { ...item, fields: [...item.fields, makeField()] }
                        : item,
                    ),
                  )
                }
              >
                Add Field
              </Button>
            </Group>
          </div>
        ))
      )}

      <Group mb="lg">
        <Button
          variant="secondary"
          iconLeft={<IconPlus size={16} />}
          onClick={() => updateSections((previous) => [...previous, makeSection(previous.length)])}
        >
          Add Section
        </Button>
        <Text size="sm" c="dimmed">
          {fieldCount} field{fieldCount === 1 ? "" : "s"} total
        </Text>
      </Group>

      <PublishTemplateModal
        opened={publishOpened}
        onClose={() => setPublishOpened(false)}
        onConfirm={handlePublish}
        submitting={publishMutation.isPending || saveMutation.isPending}
      />

      <Modal
        isOpen={previewOpened}
        onClose={() => setPreviewOpened(false)}
        size={640}
        title={`${version.templateName} — Preview`}
      >
        {fieldCount === 0 ? (
          <Text c="dimmed" py="lg" ta="center">
            This version has no fields yet.
          </Text>
        ) : (
          <PreviewSections sections={sections} />
        )}
      </Modal>
    </>
  );
}
